<template>
  <div id="components-card-license">
    <vx-card :title="licenseCategoryName" :subtitle="licenseGrade">
      <template slot="actions">
        <license-action-dropdown
          :hasImage="hasImage"
          @licenceAction="licenceAction"
        ></license-action-dropdown>
      </template>
      <div
        :id="licensePictureId"
        class="image-area-wrapper vs-con-loading__container"
      >
        <div>
          <feather-icon
            class="no-thumbnail-error-img"
            v-if="thumbnailErrorMsg"
            icon="AlertCircleIcon"
          ></feather-icon>
          <p v-html="thumbnailErrorMsg" class="no-thumbnail-error-text"></p>
        </div>
        <img id="print" :src="imgSrc" class="image-area" />
      </div>

      <light-box v-if="media.length > 0"
        ref="lightbox"
        :media="media"
        :show-light-box="false"
        :show-thumbs="false"
        :show-caption="false"
        :disableScroll="false"
      >
      </light-box>

      <div class="license-memo">{{ data.memo }}</div>

      <vs-alert
        :color="expireDateColor"
        :title="expireDateTitle"
        active="true"
        class="mt-5"
      >
        {{ expireDate }}
        まで有効
      </vs-alert>
    </vx-card>

    <vs-popup
      class="popup-license-edit"
      :title="'免許・資格の編集'"
      :active.sync="popupActive"
    >
      <div>
        <license-edit
          class="mt-4"
          ref="licenseEdit"
          :payload="payload"
          @closePopup="popupActive = false"
        />
      </div>
    </vs-popup>

  </div>
</template>

<script>
import { storage } from "@/firebase/firebaseConfig";
import LicenseMixin from "@/mixins/LicenseMixin";
import * as DateUtil from "@/utils/DateUtil.js";
import LicenseActionDropdown from "@/components/dropdown/LicenceActionDropdown";
import { PrintUtil, DownloadUtil } from "@/utils";
import LightBox from "vue-image-lightbox";
import LicenseEdit from "@/views/enterprise/management/user/license-edit/LicenseEdit"


import { AiseaApiFactory } from "@/api/aisea/apiFactory";
const picApi = AiseaApiFactory.get("picture");
const eplApi = AiseaApiFactory.get("enterpriseLicense");

export default {
  name: "cardLicense",
  components: { LicenseActionDropdown, LightBox, LicenseEdit },
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  mixins: [LicenseMixin],
  data() {
    return {
      media: [],
      hasImage: false,
      imgSrc: null,
      userId: null,
      dataURI: null,
      imageLoadingStart: false,
      licensePictureId: null,
      timer: null,
      thumbnailGetRetryCount: 0,
      thumbnailErrorMsg: null,
      popupActive: false,
      payload: {},
    };
  },
  computed: {
    licenseCategoryName() {
      return this.getLicenseCategoryLabel(this.data.license_type_id);
    },
    licenseGrade() {
      if (this.isOthersLicense(Number(this.data.license_type_id)))
        return this.data.name;
      return this.findLicenseLabelById(this.data.license_type_id);
    },
    expireDate() {
      let date = DateUtil.toDate(this.data.expiration_date);
      return DateUtil.toEMD_ja(date);
    },
    expireDateColor() {
      let expireDate = DateUtil.toMomentObj(this.data.expiration_date);
      let diffDate = DateUtil.diffDays(expireDate);
      if (diffDate >= 1) {
        return "danger";
      } else if (diffDate >= -30 && diffDate <= 0) {
        return "warning";
      } else {
        return "success";
      }
    },
    expireDateTitle() {
      let expireDate = DateUtil.toMomentObj(this.data.expiration_date);
      let diffDate = DateUtil.diffDays(expireDate);
      if (diffDate >= 1) {
        return "有効期限が切れています";
      } else if (diffDate >= -30 && diffDate <= 0) {
        return `期限切れまで残り${-diffDate + 1}日です`;
      } else {
        return "有効です";
      }
    }
  },
  methods: {
    getThumbnailImageSrc() {
      if (!this.imageLoadingStart) {
        this.$vs.loading({
          type: "border",
          container: `#${this.licensePictureId}`,
          scale: 0.6
        });
        this.imageLoadingStart = true;
      }
      storage
        .child(this.data.pictures[0].picture.thumbnail_path)
        .getDownloadURL()
        .then(url => {
          this.imgSrc = url;
          this.$vs.loading.close(`#${this.licensePictureId} > .con-vs-loading`);
          this.imageLoadingStart = false;
          this.thumbnailGetRetryCount = 0;
        })
        .catch(error => {
          this.retryGetImage(error);
        });
    },

    getOriginalImageSrc() {
      storage
        .child(this.data.pictures[0].picture.original_path)
        .getDownloadURL()
        .then(url => {
          this.media = [
            {
              src: url
            }
          ];
          this.thumbnailGetRetryCount = 0;
        })
        .catch(error => {
          this.retryGetImage(error);
        });
    },

    retryGetImage(error) {
      if (this.thumbnailGetRetryCount === 5) {
        clearTimeout(this.timer);
        this.$vs.loading.close(`#${this.licensePictureId} > .con-vs-loading`);
        this.imageLoadingStart = false;
        this.thumbnailErrorMsg = "画像の取得に失敗";
        this.thumbnailGetRetryCount = 0;
      } else {
        if (error.code === "storage/object-not-found") {
          this.thumbnailGetRetryCount = this.thumbnailGetRetryCount + 1;
          this.timer = setTimeout(() => {
            this.getThumbnailImageSrc();
          }, 3000);
        }
      }
    },

    licenceAction(evt) {
      if (evt === "edit") {
        this.editLicense();
        return;
      }

      if (evt === "print") {
        this.printLicense();
        return;
      }

      if (evt === "delete") {
        this.openDeleteConfirm();
        return;
      }

      if (evt === "download") {
        this.download();
        return;
      }

      if (evt === "preview") {
        this.showPreview();
        return;
      }
    },

    editLicense() {
      let payload = {
        userId: this.userId,
        imgSrc: this.hasImage ? this.imgSrc : null,
        license: {
          id: this.data.id,
          name: this.data.name,
          licenseTypeId: this.findLicenseOption(this.data.license_type_id),
          expirationDate: this.data.expiration_date,
          memo: this.data.memo,
          pictures: this.data.pictures
        }
      };
      this.payload = payload;
      this.popupActive = true;
    },

    openDeleteConfirm() {
      this.$vs.dialog({
        type: "confirm",
        color: "danger",
        title: `免許・資格の削除`,
        text: `「${this.getLicenseTypeText()}」を削除しますか？`,
        acceptText: "削除する",
        cancelText: "キャンセル",
        accept: this.deleteLicense
      });
    },

    showPreview() {
      if (this.data.pictures[0].picture.original_path.includes(".pdf")) {
        storage
          .child(this.data.pictures[0].picture.original_path)
          .getDownloadURL()
          .then(response => {
            window.open(response, "_blank");
          });
      } else {
        this.$refs.lightbox.showImage(0);
      }
    },

    download() {
      !this.dataURI
        ? this.downloadFile("ダウンロードに失敗しました", true)
        : DownloadUtil.saveURLs(this.dataURI, this.getLicenseTypeText());
    },

    async downloadFile(titleError, makeFile) {
      this.$vs.loading();
      try {
        let res = await picApi.download(this.data.pictures[0].picture.id);
        let blob = res.data;
        let reader = new FileReader();
        reader.onload = e => {
          this.dataURI = e.target.result;
          makeFile
            ? DownloadUtil.saveURLs(this.dataURI, this.getLicenseTypeText())
            : this.printLicense();
          this.$vs.loading.close();
        };
        reader.readAsDataURL(blob);
      } catch (e) {
        this.$vs.loading.close();
        this.$vs.notify({
          title: titleError,
          color: "danger",
          iconPack: "feather",
          icon: "icon-x-square"
        });
      }
    },
    printLicense() {
      if (!this.dataURI) {
        this.downloadFile("プリントに失敗しました", false);
        return;
      }
      this.data.pictures[0].picture.original_path.includes(".pdf")
        ? PrintUtil.printPdf(this.dataURI)
        : PrintUtil.printImage(this.dataURI);
    },
    async deleteLicense() {
      this.$vs.loading();
      try {
        await eplApi.delete(this.userId, this.data.id);
        this.$vs.loading.close();
        this.$vs.notify({
          color: "danger",
          title: this.getLicenseTypeText() + "を削除しました"
        });
        this.$vs.loading.close();
        this.$emit("updateLicenses");
      } catch (e) {
        this.$vs.loading.close();
        this.$vs.notify({
          title: "削除に失敗しました",
          color: "danger",
          iconPack: "feather",
          icon: "icon-x-square"
        });
      }
    },
    getLicenseTypeText() {
      let text = "";
      if (!this.isOthersLicense(Number(this.data.license_type_id))) {
        let category = this.getLicenseCategoryLabel(this.data.license_type_id);
        let grade = this.findLicenseLabelById(this.data.license_type_id);
        text = `${category} ${grade}`;
      } else {
        text = this.data.name;
      }
      return text;
    },
    updateImage() {
      if (this.data.pictures.length !== 0) {
        this.getThumbnailImageSrc();
        this.getOriginalImageSrc();
        this.hasImage = true;
      } else {
        this.imgSrc = require("@/assets/images/no_image.png");
        this.hasImage = false;
      }
    }
  },
  watch: {
    popupActive() {
      if ( this.popupActive === false ) {
        // 免許・資格編集ダイアログから戻ったら、データ取り直し
        this.$emit("updateLicenses");
      }
    },
    data() {
      // 親からのライセンスデータが更新されたら、画像情報も更新
      this.updateImage();
    }
  },
  created() {
    this.userId = this.$route.params.userId;
    this.licensePictureId = "license-picture-" + this.data.id;
  },
  mounted() {
    this.updateImage();
  }
};
</script>
<style lang="scss">
#components-card-license {
  height: 100%;

  > .vx-card {
    height: 100%;
  }

  .image-area-wrapper {
    height: 15rem;

    .image-area {
      width: 100%;
      height: 100%;
      min-width: 100%;
      min-height: 100%;
      border-radius: 8px;
      filter: drop-shadow(0px 0px 5px rgba(0, 0, 0, 0.4));
      object-fit: contain;
    }
    .no-thumbnail-error-img {
      color: #c3c3c3;
      position: absolute;
      top: 40%;
      left: 50%;
      -ms-transform: translate(-50%, -50%);
      -webkit-transform: translate(-50%, -50%);
      transform: translate(-50%, -50%);
      margin: 0;
      padding: 0;
    }
    .no-thumbnail-error-text {
      font-size: smaller;
      color: #c3c3c3;
      position: absolute;
      top: 55%;
      left: 50%;
      -ms-transform: translate(-50%, -50%);
      -webkit-transform: translate(-50%, -50%);
      transform: translate(-50%, -50%);
      margin: 0;
      padding: 0;
    }
  }
  .vx-card__title {
    overflow: hidden;
    width: 85%;
    h6 {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }
  }
  .license-memo {
    width: 100%;
    height: 6rem;
    overflow-y: auto;
    white-space: pre-wrap;
    padding: 0.6rem 0.2rem;
  }
}
.popup-license-edit .vs-popup {
  width: calc(100vw - 20px - 260px - 260px);
  z-index: 10;
}
</style>
